import React, { useState, useEffect, useCallback } from 'react';
import './Admin.css';
import { getAuth, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, deleteDoc, doc, query, orderBy, updateDoc, limit, startAfter, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

const JOBS_KEY = 'dudigital_pending_jobs';
const PAGE_SIZE = 20;

const loadJobs = () => { try { return JSON.parse(localStorage.getItem(JOBS_KEY)) || []; } catch { return []; } };
const saveJobs = (jobs) => localStorage.setItem(JOBS_KEY, JSON.stringify(jobs));
const fmt = (ts) => ts?.toDate?.().toLocaleString() ?? 'Unknown date';
const fmtMoney = (n) => n != null ? '$' + Number(n).toLocaleString() : 'Unknown';

const STATUS_CONFIG = {
  pending:  { label: 'Needs Email',   color: 'amber' },
  emailed:  { label: 'Emailed',       color: 'blue'  },
  booked:   { label: 'Call Booked',   color: 'green' },
  complete: { label: 'Complete',      color: 'green' },
};

const STATUS_FLOW = {
  pending:  'emailed',
  emailed:  'booked',
  booked:   'complete',
  complete: 'pending',
};

const STATUS_BTN = {
  pending:  'Mark Emailed',
  emailed:  'Mark Booked',
  booked:   'Mark Complete',
  complete: 'Reopen',
};

export default function Admin() {
  const auth = getAuth();

  const [gatePass, setGatePass] = useState('');
  const [gateOk,   setGateOk]   = useState(false);
  const [gateErr,  setGateErr]  = useState(false);
  const [user,     setUser]     = useState(null);
  const [emailVal, setEmailVal] = useState('');
  const [passVal,  setPassVal]  = useState('');
  const [loginErr, setLoginErr] = useState('');

  const [estimates,      setEstimates]      = useState([]);
  const [applications,   setApplications]   = useState([]);
  const [qualifiedLeads, setQualifiedLeads] = useState([]);
  const [loading,        setLoading]        = useState(false);

  // pagination cursors
  const [estCursor,  setEstCursor]  = useState(null);
  const [appCursor,  setAppCursor]  = useState(null);
  const [estHasMore, setEstHasMore] = useState(false);
  const [appHasMore, setAppHasMore] = useState(false);

  const [jobs,     setJobs]     = useState(loadJobs);
  const [jobInput, setJobInput] = useState('');
  const [jobNote,  setJobNote]  = useState('');
  const [tab,      setTab]      = useState('action');

  // ── AUTH ────────────────────────────────────────────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      if (u) fetchAll();
      else { setEstimates([]); setApplications([]); setQualifiedLeads([]); }
    });
    return () => unsub();
  }, []);

  // ── FETCH (paginated) ───────────────────────────────────────────
  const fetchAll = async () => {
    setLoading(true);
    try {
      const [estSnap, appSnap, qlSnap] = await Promise.all([
        getDocs(query(collection(db, 'estimates'),       orderBy('createdAt', 'desc'), limit(PAGE_SIZE))),
        getDocs(query(collection(db, 'applications'),    orderBy('createdAt', 'desc'), limit(PAGE_SIZE))),
        getDocs(query(collection(db, 'qualified_leads'), orderBy('createdAt', 'desc'), limit(100))),
      ]);
      setEstimates(estSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setEstCursor(estSnap.docs[estSnap.docs.length - 1]);
      setEstHasMore(estSnap.docs.length === PAGE_SIZE);

      setApplications(appSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setAppCursor(appSnap.docs[appSnap.docs.length - 1]);
      setAppHasMore(appSnap.docs.length === PAGE_SIZE);

      setQualifiedLeads(qlSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadMoreEstimates = async () => {
    if (!estCursor) return;
    const snap = await getDocs(query(collection(db, 'estimates'), orderBy('createdAt', 'desc'), startAfter(estCursor), limit(PAGE_SIZE)));
    setEstimates(prev => [...prev, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))]);
    setEstCursor(snap.docs[snap.docs.length - 1]);
    setEstHasMore(snap.docs.length === PAGE_SIZE);
  };

  const loadMoreApps = async () => {
    if (!appCursor) return;
    const snap = await getDocs(query(collection(db, 'applications'), orderBy('createdAt', 'desc'), startAfter(appCursor), limit(PAGE_SIZE)));
    setApplications(prev => [...prev, ...snap.docs.map(d => ({ id: d.id, ...d.data() }))]);
    setAppCursor(snap.docs[snap.docs.length - 1]);
    setAppHasMore(snap.docs.length === PAGE_SIZE);
  };

  // ── UPDATE STATUS ───────────────────────────────────────────────
  const advanceStatus = async (collectionName, id, currentStatus, setter) => {
    const next = STATUS_FLOW[currentStatus] || 'pending';
    try {
      await updateDoc(doc(db, collectionName, id), { status: next, completed: next === 'complete' });
      setter(prev => prev.map(r => r.id === id ? { ...r, status: next, completed: next === 'complete' } : r));
    } catch (e) { console.error(e); }
  };

  const handleDelete = async (collectionName, id, setter) => {
    if (!window.confirm('Delete this record?')) return;
    await deleteDoc(doc(db, collectionName, id));
    setter(prev => prev.filter(r => r.id !== id));
  };

  // ── JOBS ────────────────────────────────────────────────────────
  const addJob = () => {
    if (!jobInput.trim()) return;
    const updated = [{ id: Date.now(), text: jobInput.trim(), note: jobNote.trim(), done: false }, ...jobs];
    setJobs(updated); saveJobs(updated); setJobInput(''); setJobNote('');
  };
  const toggleJob = (id) => { const u = jobs.map(j => j.id === id ? { ...j, done: !j.done } : j); setJobs(u); saveJobs(u); };
  const deleteJob = (id) => { const u = jobs.filter(j => j.id !== id); setJobs(u); saveJobs(u); };

  // ── EXPORT (Brevo format) ───────────────────────────────────────
  const exportEmails = () => {
    const rejectedApps = applications.filter(a => a.qualified === false);
    const rows = [
      ...qualifiedLeads.map(l => ({ FIRSTNAME: l.name || '', EMAIL: l.email })),
      ...rejectedApps.map(a  => ({ FIRSTNAME: a.name || a.firstName || '', EMAIL: a.email })),
    ];
    const seen = new Set();
    const unique = rows.filter(r => { if (!r.EMAIL || seen.has(r.EMAIL)) return false; seen.add(r.EMAIL); return true; });
    const csv  = ['EMAIL,FIRSTNAME', ...unique.map(r => r.EMAIL + ',' + r.FIRSTNAME)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'brevo_import.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  // ── DERIVED DATA ────────────────────────────────────────────────
  const rejectedApps   = applications.filter(a => a.qualified === false);
  const qualifiedApps  = applications.filter(a => a.qualified !== false);
  const jobsPending    = jobs.filter(j => !j.done).length;
  const totalEmails    = qualifiedLeads.length + rejectedApps.length;

  // Action queue — anyone with status pending or emailed (needs attention)
  const actionEstimates = estimates.filter(e => !e.completed && e.status !== 'complete');
  const actionApps      = qualifiedApps.filter(a => !a.completed && a.status !== 'complete');
  const actionRejected  = rejectedApps.filter(a => (a.status || 'pending') !== 'complete');
  const actionCount     = actionEstimates.length + actionApps.length + actionRejected.length;
const handleGate = async () => {
  try {
    const snap = await getDoc(doc(db, 'config', 'admin'));
    if (snap.exists() && snap.data().gateCode === gatePass) {
      setGateOk(true);
    } else {
      setGateErr(true);
    }
  } catch (e) {
    setGateErr(true);
  }
};
  // ── GATE ────────────────────────────────────────────────────────
  if (!gateOk) return (
    <div className="admin-wrap">
      <div className="admin-login">
        <div className="login-card">
          <div className="login-logo">Du<span>Digital</span></div>
          <div className="login-subtitle">Admin Dashboard - Authorized Access Only</div>
          <label className="login-label">Access Code</label>
          <input className="login-input" type="password" placeholder="Enter code" value={gatePass}
            onChange={e => { setGatePass(e.target.value); setGateErr(false); }}
          onKeyDown={e => { if (e.key === 'Enter') handleGate(); }}
            autoFocus />
         <button className="login-btn" onClick={handleGate}>Continue</button>
          {gateErr && <div className="login-error">Incorrect access code.</div>}
        </div>
      </div>
    </div>
  );

  // ── LOGIN ───────────────────────────────────────────────────────
  if (!user) return (
    <div className="admin-wrap">
      <div className="admin-login">
        <div className="login-card">
          <div className="login-logo">Du<span>Digital</span></div>
          <div className="login-subtitle">Sign in to continue</div>
          <form onSubmit={async e => { e.preventDefault(); setLoginErr(''); try { await signInWithEmailAndPassword(auth, emailVal, passVal); } catch { setLoginErr('Invalid email or password.'); }}}>
            <label className="login-label">Email</label>
            <input className="login-input" type="email" value={emailVal} onChange={e => setEmailVal(e.target.value)} required placeholder="matt@dudigital.ca" />
            <label className="login-label">Password</label>
            <input className="login-input" type="password" value={passVal} onChange={e => setPassVal(e.target.value)} required placeholder="Password" />
            <button className="login-btn" type="submit">Sign In</button>
            {loginErr && <div className="login-error">{loginErr}</div>}
          </form>
        </div>
      </div>
    </div>
  );

  // ── DASHBOARD ───────────────────────────────────────────────────
  return (
    <div className="admin-wrap">
      <div className="admin-header">
        <div className="admin-header-logo">Du<span>Digital</span> Admin</div>
        <div className="admin-header-right">
          <span className="admin-user">{user.email}</span>
          <button className="logout-btn" onClick={() => signOut(auth)}>Sign Out</button>
        </div>
      </div>

      <div className="admin-body">

        {/* STATS */}
        <div className="stats-row">
          <div className="stat-chip">
            <div className="stat-chip-num amber">{actionCount}</div>
            <div className="stat-chip-label">Need Action</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-num blue">{estimates.length}</div>
            <div className="stat-chip-label">Estimates</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-num blue">{qualifiedApps.length}</div>
            <div className="stat-chip-label">Qualified Apps</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-num" style={{color:'var(--red)'}}>{rejectedApps.length}</div>
            <div className="stat-chip-label">Rejected Apps</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-num amber">{jobsPending}</div>
            <div className="stat-chip-label">Pending Jobs</div>
          </div>
          <div className="stat-chip">
            <div className="stat-chip-num green">{totalEmails}</div>
            <div className="stat-chip-label">Email List</div>
          </div>
        </div>

        {/* TABS */}
        <div className="admin-tabs">
          {[
            { id: 'action',       label: 'Action Queue',      count: actionCount        },
            { id: 'estimates',    label: 'All Estimates',      count: estimates.length   },
            { id: 'applications', label: 'Website Apps',       count: qualifiedApps.length },
            { id: 'rejected',     label: 'Rejected',           count: rejectedApps.length  },
            { id: 'jobs',         label: 'Pending Jobs',       count: jobsPending        },
            { id: 'leads',        label: 'Mailing List',       count: totalEmails        },
          ].map(t => (
            <button key={t.id} className={`tab-btn${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>
              {t.label}<span className="tab-count">{t.count}</span>
            </button>
          ))}
        </div>

        {loading && <div style={{color:'var(--text-sub)', padding:'20px 0'}}>Loading...</div>}

        {/* ── ACTION QUEUE ── */}
        {tab === 'action' && !loading && (
          <>
            <div className="section-head">
              <div>
                <h2>Action Queue</h2>
                <p>Everyone who needs a follow-up email or check-in, sorted by priority.</p>
              </div>
            </div>

            {actionCount === 0 ? (
              <div className="empty-state"><div className="empty-icon">🎉</div>You are all caught up! Nothing needs action right now.</div>
            ) : (
              <>
                {/* Qualified apps not yet booked */}
                {actionApps.filter(a => a.status === 'pending' || a.status === 'emailed').length > 0 && (
                  <>
                    <div className="leads-group-label">
                      Free Website - Qualified
                      <div className="action-hint">
                        <span className="hint-tag pending-hint">First email:</span> "Hey [name], great news — you've been approved for a free website! Looking forward to working with you. Have you had a chance to book a time to meet? Here's the link: calendly.com/..."
                        <br/>
                        <span className="hint-tag emailed-hint">Follow-up:</span> "Hey [name], just checking in — did you get a chance to book a time? Would love to get started on your site!"
                      </div>
                    </div>
                    {actionApps.map(app => (
                      <RecordCard key={app.id} record={app} type="app"
                        onAdvance={() => advanceStatus('applications', app.id, app.status || 'pending', setApplications)}
                        onDelete={() => handleDelete('applications', app.id, setApplications)} />
                    ))}
                  </>
                )}

                {/* Rejected apps — send paid quote */}
                {rejectedApps.filter(a => (a.status || 'pending') !== 'complete').length > 0 && (
                  <>
                    <div className="leads-group-label" style={{marginTop:'24px'}}>
                      Free Website - Rejected
                      <div className="action-hint">
                        <span className="hint-tag pending-hint">Suggested email:</span> "Hey [name], thanks for applying! Your project is a bit more advanced than our free program covers — but we'd love to help. We build custom sites and apps at competitive rates. Want to jump on a free 15-min call to talk through what it would cost?"
                      </div>
                    </div>
                    {rejectedApps.filter(a => (a.status || 'pending') !== 'complete').map(app => (
                      <RecordCard key={app.id} record={app} type="rejected"
                        onAdvance={() => advanceStatus('applications', app.id, app.status || 'pending', setApplications)}
                        onDelete={() => handleDelete('applications', app.id, setApplications)} />
                    ))}
                  </>
                )}

                {/* Estimates not actioned */}
                {actionEstimates.length > 0 && (
                  <>
                    <div className="leads-group-label" style={{marginTop:'24px'}}>
                      Price Estimates
                      <div className="action-hint">
                        <span className="hint-tag pending-hint">Suggested email:</span> "Hey [name], thanks for using our estimator! Based on what you described, I'd love to give you a more accurate quote. Want to jump on a free 15-min call? No pressure at all — just a quick chat."
                      </div>
                    </div>
                    {actionEstimates.map(est => (
                      <RecordCard key={est.id} record={est} type="estimate"
                        onAdvance={() => advanceStatus('estimates', est.id, est.status || 'pending', setEstimates)}
                        onDelete={() => handleDelete('estimates', est.id, setEstimates)} />
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* ── ALL ESTIMATES ── */}
        {tab === 'estimates' && !loading && (
          <>
            <div className="section-head">
              <div><h2>All Estimates</h2><p>{estimates.length} total</p></div>
            </div>
            {estimates.length === 0
              ? <div className="empty-state"><div className="empty-icon">📋</div>No estimates yet.</div>
              : estimates.map(est => (
                <RecordCard key={est.id} record={est} type="estimate"
                  onAdvance={() => advanceStatus('estimates', est.id, est.status || 'pending', setEstimates)}
                  onDelete={() => handleDelete('estimates', est.id, setEstimates)} />
              ))}
            {estHasMore && <button className="load-more-btn" onClick={loadMoreEstimates}>Load More</button>}
          </>
        )}

        {/* ── WEBSITE APPS ── */}
        {tab === 'applications' && !loading && (
          <>
            <div className="section-head">
              <div><h2>Qualified Applications</h2><p>{qualifiedApps.length} total</p></div>
            </div>
            {qualifiedApps.length === 0
              ? <div className="empty-state"><div className="empty-icon">📝</div>No qualified applications yet.</div>
              : qualifiedApps.map(app => (
                <RecordCard key={app.id} record={app} type="app"
                  onAdvance={() => advanceStatus('applications', app.id, app.status || 'pending', setApplications)}
                  onDelete={() => handleDelete('applications', app.id, setApplications)} />
              ))}
            {appHasMore && <button className="load-more-btn" onClick={loadMoreApps}>Load More</button>}
          </>
        )}

        {/* ── REJECTED ── */}
        {tab === 'rejected' && !loading && (
          <>
            <div className="section-head">
              <div><h2>Rejected Applications</h2><p>{rejectedApps.length} total - consider sending a paid quote</p></div>
            </div>
            {rejectedApps.length === 0
              ? <div className="empty-state"><div className="empty-icon">✓</div>No rejected applications.</div>
              : rejectedApps.map(app => (
                <RecordCard key={app.id} record={app} type="rejected"
                  onAdvance={() => advanceStatus('applications', app.id, app.status || 'pending', setApplications)}
                  onDelete={() => handleDelete('applications', app.id, setApplications)} />
              ))}
          </>
        )}

        {/* ── PENDING JOBS ── */}
        {tab === 'jobs' && (
          <>
            <div className="section-head">
              <div><h2>Pending Jobs</h2><p>{jobsPending} remaining - stored locally on this device</p></div>
            </div>
            <div className="jobs-input-row">
              <input className="job-input" placeholder="Job title or client name..."
                value={jobInput} onChange={e => setJobInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && addJob()} />
              <input className="job-input" placeholder="Note (optional)" value={jobNote}
                onChange={e => setJobNote(e.target.value)} style={{maxWidth:'220px'}}
                onKeyDown={e => e.key === 'Enter' && addJob()} />
              <button className="add-job-btn" onClick={addJob}>+ Add Job</button>
            </div>
            {jobs.length === 0
              ? <div className="empty-state"><div className="empty-icon">✅</div>No pending jobs. You are all caught up!</div>
              : jobs.map(job => (
                <div key={job.id} className={`job-item${job.done ? ' done' : ''}`}>
                  <div className={`job-checkbox${job.done ? ' checked' : ''}`} onClick={() => toggleJob(job.id)} />
                  <span className="job-text">{job.text}</span>
                  {job.note && <span className="job-note">{job.note}</span>}
                  <button className="job-delete" onClick={() => deleteJob(job.id)}>x</button>
                </div>
              ))}
          </>
        )}

        {/* ── MAILING LIST ── */}
        {tab === 'leads' && !loading && (
          <>
            <div className="section-head">
              <div><h2>Mailing List</h2><p>{totalEmails} emails - Brevo-ready export</p></div>
              <button className="export-btn" onClick={exportEmails}>Export CSV (Brevo)</button>
            </div>
            {totalEmails === 0
              ? <div className="empty-state"><div className="empty-icon">📧</div>No leads yet.</div>
              : [...qualifiedLeads, ...rejectedApps].map(l => (
                <div key={l.id} className="record-card">
                  <div className="record-top">
                    <div>
                      <div className="record-name">{l.name || l.firstName}</div>
                      <a href={'mailto:' + l.email} className="record-email">{l.email}</a>
                    </div>
                    <div className="record-date" style={{marginTop:0}}>{fmt(l.createdAt)}</div>
                  </div>
                </div>
              ))}
          </>
        )}

      </div>
    </div>
  );
}

// ── RECORD CARD COMPONENT ─────────────────────────────────────────
function RecordCard({ record, type, onAdvance, onDelete }) {
  const status = record.status || 'pending';
  const cfg    = STATUS_CONFIG[status] || STATUS_CONFIG.pending;

  return (
    <div className={`record-card ${record.completed ? 'complete' : status === 'emailed' ? 'emailed' : 'pending'}`}>
      <div className="record-top">
        <div>
          <div className="record-name">{record.name || record.firstName || 'Unknown'}</div>
          <a href={'mailto:' + record.email} className="record-email">{record.email}</a>
          {record.phone && (
            <a href={'tel:' + record.phone.replace(/[^\d+]/g, '')} className="record-email" style={{ display: 'block' }}>
              📞 {record.phone}
            </a>
          )}
        </div>
        <div className="record-actions">
          <span className={`status-badge ${cfg.color}`}>{cfg.label}</span>
          <button className={`action-btn ${record.completed ? 'uncomplete-btn' : 'complete-btn'}`} onClick={onAdvance}>
            {STATUS_BTN[status] || 'Advance'}
          </button>
          <button className="action-btn delete-btn" onClick={onDelete}>Delete</button>
        </div>
      </div>

      <div className="record-meta">
        {type === 'estimate' && (
          <>
            {record.projectType && <span className="meta-pill blue">{record.projectType.toUpperCase()}</span>}
            {record.pages       && <span className="meta-pill">{record.pages} pages</span>}
            {record.estimatedLow != null && (
              <span className="meta-pill green">{fmtMoney(record.estimatedLow)} - {fmtMoney(record.estimatedHigh)}</span>
            )}
          </>
        )}
        {(type === 'app' || type === 'rejected') && (
          <>
            {record.pages && <span className="meta-pill">{record.pages} pages</span>}
            {record.disqSelected?.map(f => <span key={f} className="meta-pill amber">{f}</span>)}
          </>
        )}
      </div>

      {record.description && <div className="record-desc">{record.description}</div>}
      <div className="record-date">{fmt(record.createdAt)}</div>
    </div>
  );
}